import type { CABTag } from '../types/cabTags';

export const TAG_PATTERN = /\{\{tag:([^|]+)\|([^}]+)\}\}/g;

export function serializeTagsIntoContent(content: string, tags: CABTag[]): string {
  if (!tags.length) return content;

  let result = content;
  
  for (const tag of tags) {
    const tagMarker = `@${tag.label}`;
    const jsonPath = tag.jsonPath.join('.');
    const serialized = `{{tag:${jsonPath}|${tag.label}}}`;
    result = result.replace(tagMarker, serialized);
  }

  return result;
}

export interface ParsedTag {
  id: string;
  label: string;
  jsonPath: string[];
  startIndex: number;
  endIndex: number;
}

export interface ParsedContent {
  tags: ParsedTag[];
  segments: Array<{ type: 'text' | 'tag'; content: string; tag?: ParsedTag }>;
}

export function parseTagsFromContent(content: string): ParsedContent {
  const tags: ParsedTag[] = [];
  const segments: ParsedContent['segments'] = [];
  
  let lastIndex = 0;
  let match;
  const regex = new RegExp(TAG_PATTERN.source, 'g');

  while ((match = regex.exec(content)) !== null) {
    const [fullMatch, jsonPathStr, label] = match;
    const startIndex = match.index;
    const endIndex = startIndex + fullMatch.length;

    if (startIndex > lastIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastIndex, startIndex),
      });
    }

    const jsonPath = jsonPathStr.split('.');
    const tag: ParsedTag = {
      id: `${jsonPathStr}-${label}`,
      label,
      jsonPath,
      startIndex,
      endIndex,
    };

    tags.push(tag);
    segments.push({
      type: 'tag',
      content: label,
      tag,
    });

    lastIndex = endIndex;
  }

  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastIndex),
    });
  }

  return { tags, segments };
}

export function hasSerializedTags(content: string): boolean {
  return new RegExp(TAG_PATTERN.source).test(content);
}

export function stripTagMarkers(content: string): string {
  return content.replace(TAG_PATTERN, '@$2');
}
